import type { DesktopHandoffRouteFamily } from './constants';
import type { DesktopDeviceMode } from './device-cookie';

import { OPEN_IN_BROWSER, OPEN_IN_PARAM } from './constants';
import {
  getDesktopHandoffRouteFamily,
  getEligibleHandoffTarget,
  validateHandoffTarget,
} from './eligibility';

import { safeSessionStorage } from '@/lib/safe-storage';
import { buildDeepLink } from '@/lib/shared/deep-link-types';

/** A user counts as having the desktop app when it reported in within this window. */
export const DESKTOP_APP_RECENT_MS = 30 * 24 * 60 * 60 * 1000;

/** Reloading or coming back to the tab this soon after a launch does not offer it again. */
export const RECENT_LAUNCH_MS = 10_000;

const DISMISSED_STORAGE_KEY = 'desktop-open-in-app:dismissed';
const LAST_LAUNCH_STORAGE_KEY = 'desktop-open-in-app:last-launch';

type OpenInAppTarget = { target: string; routeFamily: DesktopHandoffRouteFamily };

export type OpenInAppDecision = { kind: 'none' } | ({ kind: 'offer' } & OpenInAppTarget);

const NONE: OpenInAppDecision = { kind: 'none' };

/** Whether the desktop app reported in for this person within `DESKTOP_APP_RECENT_MS`. */
export function hasRecentDesktopApp(lastSeenAt: string | null, now: number): boolean {
  const seenAt = lastSeenAt ? Date.parse(lastSeenAt) : Number.NaN;
  return Number.isFinite(seenAt) && now - seenAt < DESKTOP_APP_RECENT_MS;
}

/**
 * Whether to offer to open the page a user landed on in the browser in the desktop app. Browsers
 * with the desktop cookie qualify, and so do signed-in users whose app reported in recently, but
 * only on PR and inbox pages. It only ever offers. With "Always open links in the app" on, the
 * proxy opens these pages in the app before they render (`src/proxy.ts`).
 */
export function decideOpenInApp({
  url,
  deviceMode,
  desktopAppLastSeenAt,
  now,
}: {
  url: string;
  deviceMode: DesktopDeviceMode | null;
  desktopAppLastSeenAt: string | null;
  now: number;
}): OpenInAppDecision {
  if (deviceMode === null && !hasRecentDesktopApp(desktopAppLastSeenAt, now)) {
    return NONE;
  }

  const location = new URL(url);
  const target = getEligibleHandoffTarget(location);
  const targetUrl = target ? new URL(target, location.origin) : null;
  const routeFamily = targetUrl ? getDesktopHandoffRouteFamily(targetUrl.pathname) : null;
  const lastLaunchAt = Number(safeSessionStorage.getItem(LAST_LAUNCH_STORAGE_KEY));
  // `open_in` is read from the target, which a sign-in page carries in its `returnTo`.
  if (
    !target ||
    !routeFamily ||
    targetUrl?.searchParams.get(OPEN_IN_PARAM) === OPEN_IN_BROWSER ||
    now - lastLaunchAt < RECENT_LAUNCH_MS
  ) {
    return NONE;
  }

  return safeSessionStorage.getItem(DISMISSED_STORAGE_KEY) === 'true'
    ? NONE
    : { kind: 'offer', target, routeFamily };
}

/** "Not now" hides the offer for the rest of the browser session. */
export function dismissOpenInApp(): void {
  safeSessionStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
}

/**
 * The `cubic://` link that opens `path` in the desktop app. The running app claims only PR and
 * inbox links, and ToDesktop loads any other link under its `/inbox` start URL, where only those
 * two have redirects back. Any other path opens the inbox instead of a 404.
 */
export function buildDesktopAppLink(path: string): string {
  return buildDeepLink(validateHandoffTarget(path) ?? '/inbox');
}

/**
 * Opens an eligible PR or inbox path in the desktop app through the `cubic://` protocol. A running
 * app handles it without reloading; otherwise the OS starts the app on that page.
 */
export function launchInDesktopApp(target: string): void {
  safeSessionStorage.setItem(LAST_LAUNCH_STORAGE_KEY, String(Date.now()));
  window.location.assign(buildDesktopAppLink(target));
}
